import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListShortShipmentsInput } from "@nexxt/contracts/sales";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the shipments closed short (50-api/sales.md, ADR-199): Finance's list, and one with its lines.

const shortShipments = api.sales["short-shipments"];

type ListPage = InferResponseType<typeof shortShipments.$get, 200>;
export type ShortShipment = ListPage["items"][number];
export type ShortShipmentDetail = InferResponseType<(typeof shortShipments)[":shortShipmentId"]["$get"], 200>;

/** The filters of the list input (`listShortShipmentsInput`), without the paging the queries add. */
export type ShortShipmentFilters = Pick<ListShortShipmentsInput, "q"> & { credited?: boolean | undefined };

const PAGE_SIZE = 50;

const keys = { all: ["short-shipments"] as const };

export const shortShipmentQueries = {
  ...keys,

  list: (filters: ShortShipmentFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "list", filters],
      queryFn: ({ pageParam, signal }) => {
        const { credited, ...rest } = filters;
        // false is a filter too, the queue of Finance: `filterQuery` leaves out what is not set
        const query = {
          ...filterQuery(rest),
          ...(credited !== undefined && { credited: credited ? ("true" as const) : ("false" as const) }),
          page: String(pageParam),
          pageSize: String(PAGE_SIZE),
        };
        return unwrap(shortShipments.$get({ query }, { init: { signal } }));
      },
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  detail: (shortShipmentId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", shortShipmentId],
      queryFn: ({ signal }) =>
        unwrap(shortShipments[":shortShipmentId"].$get({ param: { shortShipmentId } }, { init: { signal } })),
    }),
};
